import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Trophy } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { HERO_COPY } from "@/lib/heroExperiment";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const WinnerDialog = ({ variant, onClose }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const pick = async () => {
    setBusy(true);
    try {
      await api.post("/admin/experiments/hero/winner", { variant });
      toast.success(`Version ${variant} is now the headline for everyone`);
      await qc.invalidateQueries({ queryKey: ["admin-hero-exp"] });
      onClose();
    } catch (e) { toast.error(errText(e, "Couldn't pick the winner")); }
    setBusy(false);
  };
  return (
    <AlertDialog open={!!variant} onOpenChange={(v) => !v && !busy && onClose()}>
      <AlertDialogContent data-testid="hero-winner-dialog" className="bg-[#121212] border-[#2A2A2A] text-white">
        <AlertDialogHeader>
          <AlertDialogTitle>Make version {variant} the headline for everyone?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#A3A3A3]">
            “{variant && HERO_COPY[variant].h1}” This ends the test for good: every visitor sees this headline and no new visits are counted. The results stay here.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="hero-winner-cancel" disabled={busy} className="rounded-full bg-transparent border-[#2A2A2A]">Keep testing</AlertDialogCancel>
          <AlertDialogAction data-testid="hero-winner-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); pick(); }}
            className="rounded-full bg-[#00FF9D] text-[#050505] hover:bg-white">{busy ? "Saving…" : "End test and use it"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const VariantCard = ({ v, winner, onPick }) => (
  <div data-testid={`hero-exp-${v.variant}`} className={`rounded-xl border p-5 ${winner === v.variant ? "border-[#00FF9D]" : "border-[#2A2A2A]"} bg-[#121212]`}>
    <p className="font-mono text-[11px] text-[#A3A3A3] flex items-center gap-2">VERSION {v.variant}{v.variant === "A" ? " · ORIGINAL" : " · NAMES THE BUYER"}
      {winner === v.variant && <span data-testid={`hero-exp-${v.variant}-winner`} className="text-[#00FF9D] flex items-center gap-1"><Trophy /> WINNER</span>}</p>
    <p className="mt-2 text-sm">{HERO_COPY[v.variant].h1}</p>
    <dl className="mt-4 grid grid-cols-4 gap-2 font-mono">
      {[["Visitors", v.visitors], ["CTA clicks", v.cta_clicks], ["Sign-ups", v.signups], ["Sign-up rate", v.signup_rate == null ? "—" : `${v.signup_rate}%`]].map(([k, n]) => (
        <div key={k}><dt className="text-[10px] text-[#A3A3A3]">{k}</dt><dd data-testid={`hero-exp-${v.variant}-${k.toLowerCase().replace(/[^a-z]/g, "")}`} className="text-xl mt-1">{n}</dd></div>
      ))}
    </dl>
    {!winner && (
      <button data-testid={`hero-pick-${v.variant}`} onClick={() => onPick(v.variant)}
        className="mt-5 rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-[#00FF9D] hover:text-[#00FF9D] transition-colors">Pick as winner</button>
    )}
  </div>
);

const Experiment = () => {
  const api = useApi();
  const [pick, setPick] = useState(null);
  const { data, error } = useQuery({ queryKey: ["admin-hero-exp"], queryFn: () => api.get("/admin/experiments/hero"), retry: false });
  if (error) return <p data-testid="admin-growth-denied" className="text-sm text-[#A3A3A3]">This page is only available to admins.</p>;
  if (!data) return <p className="text-sm text-[#A3A3A3]">Loading…</p>;
  const winner = data.winner?.variant;
  return (
    <>
      {winner && <p data-testid="hero-test-ended" className="mt-6 text-sm text-[#00FF9D]">Test ended {day(data.winner.at)}. Everyone now sees version {winner}.</p>}
      <section data-testid="hero-exp-report" className="mt-6 grid md:grid-cols-2 gap-4">
        {data.variants.map((v) => <VariantCard key={v.variant} v={v} winner={winner} onPick={setPick} />)}
      </section>
      <WinnerDialog variant={pick} onClose={() => setPick(null)} />
    </>
  );
};

const STATUS = { pending: ["Waiting for customer", "text-[#FFD166]"], approved: ["Customer said yes", "text-[#00FF9D]"], declined: ["Customer said no", "text-[#FF5C7A]"] };

const PublishSwitch = ({ r }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-feedback"] });
  const flip = async (published) => {
    try {
      await api.put(`/admin/feedback/${r.user_id}/publish`, { published });
      toast.success(published ? "Quote is live on the homepage" : "Quote removed from the homepage");
      await refresh();
    } catch (e) { toast.error(errText(e, "Couldn't update the quote")); }
  };
  const ask = async () => {
    setBusy(true);
    try { await api.post(`/admin/feedback/${r.user_id}/request-confirm`); toast.success("Confirmation email sent"); await refresh(); }
    catch (e) { toast.error(errText(e, "Couldn't send the email")); }
    setBusy(false);
  };
  if (!r.quote_ok) return <span className="text-xs text-[#A3A3A3]">Not for quoting</span>;
  const [label, color] = STATUS[r.confirm_status] || [];
  return (
    <span className="flex flex-wrap items-center gap-3">
      {label && <span data-testid={`feedback-confirm-status-${r.user_id}`} className={`text-xs ${color}`}>{label}</span>}
      {r.confirm_status !== "approved" && (
        <button data-testid={`feedback-ask-confirm-${r.user_id}`} disabled={busy} onClick={ask}
          className="rounded-full border border-[#2A2A2A] px-3 py-1 text-xs hover:border-[#00FF9D] hover:text-[#00FF9D] transition-colors disabled:opacity-50">
          {busy ? "Sending…" : r.confirm_status ? "Resend email" : "Ask to confirm"}
        </button>
      )}
      {r.confirm_status === "approved" && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch data-testid={`feedback-publish-${r.user_id}`} checked={!!r.published} onCheckedChange={flip} />
          <span className={r.published ? "text-[#00FF9D]" : "text-[#A3A3A3]"}>{r.published ? "On homepage" : "Show on homepage"}</span>
        </label>
      )}
    </span>
  );
};

const Responses = () => {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["admin-feedback"], queryFn: () => api.get("/admin/feedback"), retry: false });
  if (!data) return null;
  return (
    <section className="mt-14">
      <h2 className="font-display text-2xl">Customer feedback</h2>
      <p data-testid="feedback-invited" className="mt-1 text-sm text-[#A3A3A3]">{data.invited} of the first {data.cap} customers invited (one month after sign-up). {data.responses.length} answered. For a quote you'd like to use, send the customer a confirmation email; once they say yes, switch it on to show it above pricing.</p>
      <div className="mt-6 space-y-3">
        {data.responses.length === 0 && <p data-testid="feedback-empty" className="text-sm text-[#A3A3A3]">No answers yet.</p>}
        {data.responses.map((r) => (
          <article key={r.user_id} data-testid={`feedback-row-${r.user_id}`} className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-5">
            <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-[#A3A3A3]">
              <span className="text-white">{r.display_name || "No name given"}</span>{r.email && <span>{r.email}</span>}{r.role && <span>{r.role}</span>}
              <span>Score {r.score}/10</span><span>{day(r.submitted_at)}</span>
              <span className="ml-auto"><PublishSwitch r={r} /></span>
            </div>
            <p className="mt-3 text-sm leading-relaxed">“{r.outcome}”</p>
          </article>
        ))}
      </div>
    </section>
  );
};

export default function AdminGrowth() {
  return (
    <div data-testid="admin-growth-page" className="max-w-4xl">
      <p className="font-mono text-xs text-[#A3A3A3] flex items-center gap-2"><ShieldCheck className="text-[#00FF9D]" /> ADMIN</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Headline test & feedback</h1>
      <p className="mt-3 text-sm text-[#A3A3A3]">Visitors are split 50/50 between two homepage headlines. Only visitors who accept analytics cookies are counted. Pick a winner to end the test for good.</p>
      <Experiment />
      <Responses />
    </div>
  );
}
